import { useCallback, useEffect, useState } from 'react'
import type { MerchantTerms } from '../services/api'
import { useStoreOwner } from '../store/useStoreOwner'

/**
 * 規約の本文と同意状態 (加盟店規約 #1657 / EC 出店規約 #1710 で共通)。
 *
 * 本文は API が配信する — 同意記録に残すハッシュを**サーバ自身が計算する**必要が
 * あるため (client 送信のハッシュは証拠にならない)。
 *
 * ## 状態はモジュールで共有する
 *
 * 同じ規約を複数の場所が見る (未同意ブロックのモーダル・商品タブのロック・閲覧ページ)。
 * 各所が自分の useState を持つと、モーダルで同意しても商品タブのロックが残る
 * (別インスタンスは再取得しない) し、同じ本文を場所の数だけ取りに行く。
 * ここで `kind:uid` ごとに 1 つの状態を持ち、購読者全員に通知する。
 *
 * ## 取得できない時はブロックしない
 *
 * 規約 API の障害で店舗のレジが止まると営業できなくなる。terms=null で返し、
 * 同意はネットワークが戻れば次回求められる (ロック側は null を「未同意」扱いにしてよい)。
 */
type Entry<T> = { terms: T | null; loading: boolean; error: boolean; inflight: Promise<void> | null }

const entries = new Map<string, Entry<unknown>>()
const listeners = new Map<string, Set<() => void>>()

function entryOf<T>(key: string): Entry<T> {
  let e = entries.get(key) as Entry<T> | undefined
  if (!e) {
    e = { terms: null, loading: true, error: false, inflight: null }
    entries.set(key, e as Entry<unknown>)
  }
  return e
}

function notify(key: string) {
  listeners.get(key)?.forEach((fn) => fn())
}

function load<T>(key: string, fetcher: () => Promise<T>): Promise<void> {
  const e = entryOf<T>(key)
  if (e.inflight) return e.inflight   // 同時に複数箇所から呼ばれても 1 回だけ取りに行く
  e.loading = true
  notify(key)
  e.inflight = fetcher()
    .then((t) => { e.terms = t; e.error = false })
    .catch(() => { e.terms = null; e.error = true })
    .finally(() => { e.loading = false; e.inflight = null; notify(key) })
  return e.inflight
}

export function useTermsAgreement<T extends MerchantTerms>(
  kind: string,
  fetcher: () => Promise<T>,
  agreeFn: (version: string) => Promise<unknown>,
  /** false なら取りに行かない (この規約が関係ないオーナー)。terms は null、loading は false */
  enabled = true,
) {
  const isStoreLoggedIn = useStoreOwner((s) => s.isStoreLoggedIn)
  // uid をキーに含めて、別オーナーへの切替時に前オーナーの同意状態を残さない
  const uid = useStoreOwner((s) => s.firebaseUser?.uid) ?? ''
  const active = isStoreLoggedIn && enabled
  const key = `${kind}:${uid}`
  const [, bump] = useState(0)

  useEffect(() => {
    const fn = () => bump((n) => n + 1)
    let set = listeners.get(key)
    if (!set) {
      set = new Set()
      listeners.set(key, set)
    }
    set.add(fn)
    return () => { set!.delete(fn) }
  }, [key])

  useEffect(() => {
    if (!active) return
    const e = entryOf<T>(key)
    if (!e.terms && !e.inflight) void load(key, fetcher)
  }, [key, active, fetcher])

  const reload = useCallback(
    () => (active ? load(key, fetcher) : Promise.resolve()),
    [key, active, fetcher],
  )

  const entry = active ? entryOf<T>(key) : null
  const terms = entry?.terms ?? null

  const agree = useCallback(async () => {
    if (!terms) return
    await agreeFn(terms.currentVersion)
    await load(key, fetcher)   // 購読者全員 (モーダル・ロック・ページ) が新しい状態を見る
  }, [terms, agreeFn, key, fetcher])

  return {
    terms,
    loading: entry ? entry.loading && !entry.terms : false,
    error: entry?.error ?? false,
    reload,
    agree,
  }
}
